import { useEffect } from 'react';
import { Building2, CalendarDays, Home, SearchX, Tags } from 'lucide-react';
import { Link } from 'react-router-dom';
import { usePublicTenant } from '../hooks/usePublicTenant.js';
import { applyPublicMetadata, buildPublicPageMetadata } from '../utils/publicMetadata.js';

const destinations = [
  { to: '/', label: 'Home', icon: Home },
  { to: '/listings', label: 'Businesses', icon: Building2 },
  { to: '/offers', label: 'Deals', icon: Tags },
  { to: '/events', label: 'Events', icon: CalendarDays },
];

export default function NotFoundPage({ resource = 'page' }) {
  const { tenant, spotlightName, tenantLogoConfig, tenantLogoImageUrl } = usePublicTenant();
  const description = `The ${resource} you requested could not be found or is no longer available.`;

  useEffect(() => {
    const title = `Page not found | ${spotlightName || 'My Town Spotlight'}`;
    if (tenant) {
      applyPublicMetadata(buildPublicPageMetadata({
        tenant,
        pathname: window.location.pathname,
        siteName: spotlightName,
        cityName: tenantLogoConfig?.cityName,
        logoUrl: tenantLogoImageUrl,
        title,
        description,
        robots: 'noindex,nofollow',
        canonicalPath: '',
        jsonLd: [],
      }));
      return;
    }
    applyPublicMetadata({
      title,
      description,
      canonicalUrl: '',
      robots: 'noindex,nofollow',
      openGraph: { title, description },
      twitter: { card: 'summary', title, description },
      jsonLd: [],
    });
  }, [description, spotlightName, tenant, tenantLogoConfig?.cityName, tenantLogoImageUrl]);

  return (
    <div className="min-h-[65vh] bg-[var(--background,#f8fafc)] px-4 py-16 sm:px-6 sm:py-24">
      <section className="mx-auto max-w-3xl overflow-hidden rounded-2xl border border-[var(--border,#e2e8f0)] bg-[var(--card,#fff)] text-center shadow-sm">
        <div className="border-b border-[var(--border,#e2e8f0)] bg-[var(--surface,#f8fafc)] px-6 py-10 sm:px-10">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[var(--primary-soft,#dbeafe)] text-[var(--primary,#1d4ed8)]">
            <SearchX aria-hidden="true" className="h-8 w-8" />
          </span>
          <p className="mt-5 text-sm font-bold uppercase tracking-[0.2em] text-[var(--primary,#1d4ed8)]">404</p>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-[var(--text,#0f172a)] sm:text-4xl">Page not found</h1>
          <p className="mx-auto mt-4 max-w-xl text-base leading-7 text-[var(--muted,#64748b)]">{description}</p>
        </div>
        <nav aria-label="Helpful links" className="grid gap-px bg-[var(--border,#e2e8f0)] sm:grid-cols-4">
          {destinations.map(({ to, label, icon: Icon }) => (
            <Link key={to} to={to} className="flex min-h-24 flex-col items-center justify-center gap-2 bg-[var(--card,#fff)] px-4 py-5 font-semibold text-[var(--text,#0f172a)] transition hover:bg-[var(--surface,#f8fafc)] hover:text-[var(--primary,#1d4ed8)]">
              <Icon aria-hidden="true" className="h-5 w-5" />
              {label}
            </Link>
          ))}
        </nav>
      </section>
    </div>
  );
}
